import { Card } from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatDate } from '@/lib/utils'
import {
  Activity,
  CheckCircle,
  FileText,
  Flag,
  MessageCircle,
  Plus,
  Receipt,
  RefreshCw,
} from 'lucide-react'
import type { ProjectActivity } from '@/types/project'
import { useProjectActivities } from '@/hooks/useProjects'

interface ActivityTabProps {
  projectId: string
}

function getActivityIcon(type: ProjectActivity['type']) {
  switch (type) {
    case 'status_change':
      return RefreshCw
    case 'task_created':
      return Plus
    case 'task_completed':
      return CheckCircle
    case 'document_added':
      return FileText
    case 'milestone_completed':
      return Flag
    case 'comment':
      return MessageCircle
    case 'invoice_created':
      return Receipt
    case 'quote_created':
      return FileText
    default:
      return Activity
  }
}

function getActivityColor(type: ProjectActivity['type']) {
  switch (type) {
    case 'status_change':
      return 'bg-blue-100 text-blue-600'
    case 'task_created':
      return 'bg-purple-100 text-purple-600'
    case 'task_completed':
      return 'bg-mint-100 text-mint-600'
    case 'document_added':
      return 'bg-gold-100 text-gold-600'
    case 'milestone_completed':
      return 'bg-mint-100 text-mint-600'
    case 'comment':
      return 'bg-beige-100 text-brown-600'
    case 'invoice_created':
      return 'bg-green-100 text-green-600'
    case 'quote_created':
      return 'bg-blue-100 text-blue-600'
    default:
      return 'bg-beige-100 text-brown-600'
  }
}

function ActivityItem({ activity }: { activity: ProjectActivity }) {
  const Icon = getActivityIcon(activity.type)
  const colorClass = getActivityColor(activity.type)

  return (
    <div className="flex gap-4 py-4">
      {/* Icon */}
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}
      >
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-brown-900">{activity.description}</p>
        <div className="flex items-center gap-3 mt-1">
          {activity.user && (
            <div className="flex items-center gap-1.5">
              <Avatar
                src={activity.user.avatar_url}
                name={activity.user.full_name}
                size="xs"
              />
              <span className="text-xs text-brown-600">
                {activity.user.full_name}
              </span>
            </div>
          )}
          <span className="text-xs text-brown-400">
            {formatDate(activity.created_at, {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
    </div>
  )
}

function groupActivitiesByDate(
  activities: ProjectActivity[]
): Map<string, ProjectActivity[]> {
  const groups = new Map<string, ProjectActivity[]>()

  activities.forEach((activity) => {
    const date = new Date(activity.created_at).toDateString()
    const existing = groups.get(date) || []
    groups.set(date, [...existing, activity])
  })

  return groups
}

function formatGroupDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return "Aujourd'hui"
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Hier'
  } else {
    return formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })
  }
}

export function ActivityTab({ projectId }: ActivityTabProps) {
  const { data: activities, isLoading } = useProjectActivities(projectId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!activities || activities.length === 0) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={<Activity className="w-12 h-12" />}
          title="Aucune activite"
          description="L'historique des activites de ce projet apparaitra ici"
        />
      </Card>
    )
  }

  const groupedActivities = groupActivitiesByDate(activities)

  return (
    <Card>
      <div className="p-6">
        <h3 className="font-semibold text-brown-900 mb-4">Activite recente</h3>

        <div className="space-y-6">
          {Array.from(groupedActivities.entries()).map(([date, dayActivities]) => (
            <div key={date}>
              <h4 className="text-xs font-medium text-brown-500 uppercase tracking-wider mb-2">
                {formatGroupDate(date)}
              </h4>
              <div className="divide-y divide-beige-100">
                {dayActivities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

export default ActivityTab
